import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, Users, Calendar, FileText, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCachedPatientSearch } from "../../hooks/useCachedPatientSearch";
// import Header from "../common/Header";
// import LoadingScreen from "../common/LoadingScreen";
// import ResponseMessage from "../common/ResponseMessage";
// import axiosInstance from "../../services/axiosInstance";

interface SearchResult {
  id: number;
  type: 'patient' | 'appointment' | 'medical-record';
  title: string;
  subtitle: string;
  details?: string;
  url: string;
  score: number;
}

const GlobalSearch: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Utiliser le hook de cache pour la recherche de patients
  const {
    results: cachedResults,
    loading: searchLoading,
    error: _searchError,
    searchPatients
  } = useCachedPatientSearch();

  // Synchroniser les états avec le hook de cache
  useEffect(() => {
    setResults(cachedResults);
    setLoading(searchLoading);
  }, [cachedResults, searchLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      } else if (event.key === '/' && !isOpen) {
        event.preventDefault();
        setIsOpen(true);
        inputRef.current?.focus();
      } else if (isOpen) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedIndex(prev => (prev + 1) % results.length);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedIndex(prev => prev === 0 ? results.length - 1 : prev - 1);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          if (results[selectedIndex]) {
            navigate(results[selectedIndex].url);
            setIsOpen(false);
            setQuery("");
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, navigate]);

  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    // Annuler le timeout précédent
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Utiliser le hook de cache pour la recherche
    searchPatients(query);
  }, [query, searchPatients]);

  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      return;
    }

    searchTimeout.current = setTimeout(handleSearch, 500);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [query, handleSearch]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'patient': return <Users className="w-5 h-5" />;
      case 'appointment': return <Calendar className="w-5 h-5" />;
      case 'medical-record': return <FileText className="w-5 h-5" />;
      default: return <Search className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'patient': return 'text-blue-600 bg-blue-50';
      case 'appointment': return 'text-green-600 bg-green-50';
      case 'medical-record': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div ref={searchRef} className="relative">
      <div className="relative">
        <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Rechercher un patient (3+ caractères)... (/)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64 lg:w-80"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setResults([]);
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen && (query.length >= 3 || loading) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-slate-200 max-h-96 overflow-y-auto z-50">
          {loading ? (
            <div className="p-4 text-center text-slate-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-2 text-sm">Recherche en cours...</p>
            </div>
          ) : results.length > 0 ? (
            <div className="py-2">
              <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100">
                {results.length} résultat(s) trouvé(s) pour "{query}"
              </div>
              {results.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => {
                    navigate(result.url);
                    setIsOpen(false);
                    setQuery("");
                  }}
                  className={`w-full px-4 py-3 flex items-start space-x-3 hover:bg-slate-50 transition-colors text-left ${index === selectedIndex ? 'bg-blue-50' : ''
                    }`}
                >
                  <div className={`p-2 rounded-lg ${getTypeColor(result.type)}`}>
                    {getIcon(result.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-slate-900 truncate">{result.title}</p>
                      <div className="flex items-center space-x-2">
                        {result.score >= 90 && (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                            Exact
                          </span>
                        )}
                        <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(result.type)}`}>
                          {result.type === 'patient' ? 'Patient' : 'Dossier'}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">{result.subtitle}</p>
                    {result.details && (
                      <p className="text-xs text-slate-500 truncate">{result.details}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : query.length >= 3 && !loading ? (
            <div className="p-4 text-center text-gray-500">
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p>Aucun patient trouvé pour "{query}"</p>
              <p className="text-sm text-gray-400 mt-1">
                Vérifiez l'orthographe ou essayez avec plus de caractères
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
